import React, {
  useContext,
  useState,
  FormEvent,
  KeyboardEvent,
  useEffect,
} from "react";
import Alert from "../../parts/Alert";
import { SessionContext } from "../../layouts/OwnerLayout";
import ButtonSubmit from "../../parts/ButtonSubmit";
import FormGroup from "../../parts/FormGroup";
import AutoSuggestShopNameField from "../../parts/AutoSuggestShopNameField";
import { styled } from "styled-components";
import LinkText from "../../parts/LinkText";
import { useNavigate } from "react-router";
import { GetPublicShop200Response } from "../../../../api/v1";
import { api } from "../../../../api";
import Annotation from "../../parts/Annotation";

const EditShopFormStyle = styled(FormGroup)`
  background: none;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 10px auto;
`;

const LinkTextStyle = styled(LinkText)`
  display: block;
  font-weight: bold;
  text-align: left;
  margin-bottom: 20px;
  padding-left: 20px;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonSubmitStyle = styled(ButtonSubmit)`
  max-width: 250px;
  width: 100%;
`;

const AddExistShopForm: React.FC = () => {
  const navigation = useNavigate();
  const dayOfWeeks = [
    { id: 0, value: "日曜日", alias: "sunday" },
    { id: 1, value: "月曜日", alias: "monday" },
    { id: 2, value: "火曜日", alias: "tuesday" },
    { id: 3, value: "水曜日", alias: "wednesday" },
    { id: 4, value: "木曜日", alias: "thursday" },
    { id: 5, value: "金曜日", alias: "friday" },
    { id: 6, value: "土曜日", alias: "saturday" },
    { id: 7, value: "祝日", alias: "holiday" },
  ];
  const [shopId, setShopId] = useState<number>();
  const [shop, setShop] = useState<GetPublicShop200Response>(null);
  const [message, setMessage] = useState(null);
  const [messageLevel, setMessageLevel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isEnterPressed, setIsEnterPressed] = useState<boolean>(false);
  const session = useContext(SessionContext);

  useEffect(() => {
    if (shopId)
      api.getPublicShop(shopId).then((response) => {
        setShop(response.data);
      });
  }, [shopId]);

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      setIsEnterPressed(true);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isEnterPressed) {
      setIsEnterPressed(false);
      return;
    }
    setSubmitting(true);
    if (shop)
      await api
        .createOwnerDraftShop({
          shop: {
            name: shop.name,
            nameKana: shop.nameKana,
            description: shop.description,
            tel: shop.tel,
            address: shop.address,
            homepage: shop.homepage,
            googleMapUrl: shop.googleMapUrl,
            googleMapIframe: shop.googleMapIframe,
            nearestStation: shop.nearestStation,
            numberOfSeats: shop.numberOfSeats <= 0 ? null : shop.numberOfSeats,
            paymentMethodIds: shop.paymentMethods.map(
              (paymentMethod) => paymentMethod.id
            ),
            featureIds: shop.features.map((feature) => feature.id),
            draftBudgetsAttributes: [
              {
                timeframe: 0,
                lower:
                  shop.budgets[0].lower === 0 ? null : shop.budgets[0].lower,
                upper:
                  shop.budgets[0].upper === 0 ? null : shop.budgets[0].upper,
              },
              {
                timeframe: 1,
                lower:
                  shop.budgets[1].lower === 0 ? null : shop.budgets[1].lower,
                upper:
                  shop.budgets[1].upper === 0 ? null : shop.budgets[1].upper,
              },
              {
                timeframe: 2,
                lower:
                  shop.budgets[2].lower === 0 ? null : shop.budgets[2].lower,
                upper:
                  shop.budgets[2].upper === 0 ? null : shop.budgets[2].upper,
              },
            ],
            draftOpeningHoursAttributes: shop.openingHours.map(
              (openingHour) => ({
                dayOfWeek: dayOfWeeks.find(
                  (dayOfWeek) => dayOfWeek.alias === openingHour.dayOfWeek
                ).id,
                openingTime: openingHour.openingTime,
                closingTime: openingHour.closingTime,
              })
            ),
            images: shop.images,
            note: shop.note,
            shopId: shop.id,
            ownerId: session.ownerId,
          },
        })
        .then((response) => {
          navigation("/owner/dashboard", {
            state: {
              message: response.data.message,
              level: "success",
            },
          });
        })
        .catch((error) => {
          setMessage(error.response.data.message);
          setMessageLevel("error");
        });

    setIsEnterPressed(false);
    setSubmitting(false);
  };

  return (
    <EditShopFormStyle onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      {shop?.owner && (
        <Annotation level="warning" mb="20px">
          すでにオーナーの紐づいている店舗です。
          <br />
          申請いただいた場合は紐づいているオーナーに確認し、問題なければ申請承認となります。
          <br />
          紐づいているオーナーと申請者が同一の場合、既に紐づいているため申請却下とさせていただきます。
        </Annotation>
      )}
      <AutoSuggestShopNameField
        id="shop-name"
        placeholder="店舗名・住所から検索"
        label="店舗名・住所"
        mb="20px"
        selectShopId={shopId}
        setSelectShopIdAction={setShopId}
      />
      {shop && (
        <LinkTextStyle href={`/shops/${shop.id}`} target="_blank">
          店舗詳細を確認する
        </LinkTextStyle>
      )}
      <ButtonSubmitStyle
        value="送信"
        submitting={submitting}
        submittingMessage="送信中..."
      />
      {message && <Alert level={messageLevel}>{message}</Alert>}
    </EditShopFormStyle>
  );
};
export default AddExistShopForm;
